// EnrollmentTracking.jsx - Track enrollments, disenrollments, and rejections
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Phone,
  User,
  RefreshCw,
  ArrowRight,
  Award,
  TrendingUp
} from 'lucide-react';
import api from '../api';

export default function EnrollmentTracking() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [trackingRes, statsRes] = await Promise.all([
        api.get('/api/humana/enrollment-tracking'),
        api.get('/api/humana/enrollment-stats')
      ]);
      setData(trackingRes.data);
      setStats(statsRes.data.stats);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch enrollment data:', err);
      setError('Failed to load enrollment data. Make sure Humana is connected in Settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleCallClient = (clientId) => {
    navigate(`/clients/${clientId}?action=call`);
  };

  const handleViewClient = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const handleMarkAsSaved = async (clientId, enrollmentId) => {
    // Navigate to client profile with save modal
    navigate(`/clients/${clientId}?action=markSaved`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="animate-spin text-[#00A9E0]" size={40} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertTriangle className="mx-auto text-yellow-500 mb-4" size={40} />
          <h3 className="text-lg font-bold text-yellow-800 mb-2">Connection Required</h3>
          <p className="text-yellow-700 mb-4">{error}</p>
          <button
            onClick={() => navigate('/settings')}
            className="bg-[#00A9E0] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#007CB0] transition-all"
          >
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#172A3A]">Enrollment Tracking</h1>
          <p className="text-gray-600 mt-1">Monitor enrollments, rejections, and disenrollment requests</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition-all"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Pending Enrollments"
            value={stats.pending_enrollments || 0}
            icon={Clock}
            color="blue"
          />
          <StatCard
            label="Disenrollment Alerts"
            value={stats.pending_disenrollments || 0}
            icon={AlertTriangle}
            color="red"
          />
          <StatCard
            label="Recent Approvals"
            value={stats.recent_approvals || 0}
            icon={CheckCircle2}
            color="green"
          />
          <StatCard
            label="Recent Rejections"
            value={stats.recent_rejections || 0}
            icon={XCircle}
            color="yellow"
          />
        </div>
      )}

      {/* Disenrollment Alerts - CRITICAL */}
      {data?.disenrollments?.length > 0 && (
        <div className="mb-8">
          <div className="bg-red-50 border-2 border-red-300 rounded-2xl overflow-hidden">
            <div className="bg-red-100 px-6 py-4 flex items-center gap-3">
              <AlertTriangle className="text-red-600" size={24} />
              <h2 className="text-xl font-bold text-red-800">
                Disenrollment Alerts ({data.disenrollments.length})
              </h2>
            </div>
            <div className="p-4 space-y-4">
              {data.disenrollments.map((enrollment) => (
                <DisenrollmentCard
                  key={enrollment.id}
                  enrollment={enrollment}
                  onCall={() => handleCallClient(enrollment.client_id)}
                  onView={() => handleViewClient(enrollment.client_id)}
                  onMarkSaved={() => handleMarkAsSaved(enrollment.client_id, enrollment.id)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pending Enrollments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-blue-50 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="text-blue-600" size={20} />
              <h2 className="text-lg font-bold text-blue-800">Pending Enrollments</h2>
            </div>
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
              {data?.pending?.length || 0}
            </span>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {data?.pending?.length > 0 ? (
              <div className="space-y-3">
                {data.pending.map((enrollment) => (
                  <EnrollmentRow
                    key={enrollment.id}
                    enrollment={enrollment}
                    onClick={() => enrollment.client_id && handleViewClient(enrollment.client_id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No pending enrollments
              </div>
            )}
          </div>
        </div>

        {/* Rejections */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-yellow-50 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <XCircle className="text-yellow-600" size={20} />
              <h2 className="text-lg font-bold text-yellow-800">Rejections Requiring Action</h2>
            </div>
            <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-semibold">
              {data?.rejections?.length || 0}
            </span>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {data?.rejections?.length > 0 ? (
              <div className="space-y-3">
                {data.rejections.map((rejection) => (
                  <RejectionRow
                    key={rejection.id}
                    rejection={rejection}
                    onClick={() => rejection.client_id && handleViewClient(rejection.client_id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No recent rejections
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Win-Back Opportunities */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-purple-50 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Award className="text-purple-600" size={20} />
            <div>
              <h2 className="text-lg font-bold text-purple-800">Win-Back Opportunities</h2>
              <p className="text-sm text-purple-600">Clients who left before you started using Pro IRP</p>
            </div>
          </div>
          <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-semibold">
            {data?.winBackOpportunities?.length || 0}
          </span>
        </div>
        <div className="p-4">
          {data?.winBackOpportunities?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.winBackOpportunities.slice(0, 10).map((client) => (
                <WinBackCard
                  key={client.id}
                  client={client}
                  onClick={() => handleViewClient(client.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No win-back opportunities found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, icon: Icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className={`w-12 h-12 rounded-xl ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon size={24} />
      </div>
      <div className="text-3xl font-bold text-[#172A3A]">{value}</div>
      <div className="text-gray-600 text-sm">{label}</div>
    </div>
  );
}

// Disenrollment Alert Card
function DisenrollmentCard({ enrollment, onCall, onView, onMarkSaved }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-red-200">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <User className="text-red-600" size={24} />
          </div>
          <div>
            <div className="font-bold text-[#172A3A]">{enrollment.clientName}</div>
            <div className="text-sm text-red-600">Requesting disenrollment</div>
          </div>
        </div>
        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
          URGENT
        </span>
      </div>

      {enrollment.disenrollment_reason && (
        <div className="mt-3 p-3 bg-red-50 rounded-lg">
          <span className="text-sm text-gray-600">Reason: </span>
          <span className="text-sm text-red-700 font-medium">{enrollment.disenrollment_reason}</span>
        </div>
      )}

      <div className="mt-4 flex gap-3">
        <button
          onClick={onCall}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-all"
        >
          <Phone size={16} />
          Call Now
        </button>
        <button
          onClick={onView}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-all"
        >
          View Profile
          <ArrowRight size={16} />
        </button>
        <button
          onClick={onMarkSaved}
          className="flex items-center gap-2 px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg font-semibold transition-all ml-auto"
        >
          <CheckCircle2 size={16} />
          Mark as Saved
        </button>
      </div>
    </div>
  );
}

// Enrollment Row
function EnrollmentRow({ enrollment, onClick }) {
  return (
    <div
      onClick={onClick}
      className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-all"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-[#172A3A]">{enrollment.clientName || 'Unknown'}</div>
          <div className="text-sm text-gray-500">
            {enrollment.plan_name || enrollment.plan_type || 'New Enrollment'}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {enrollment.submitted_at && new Date(enrollment.submitted_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

// Rejection Row
function RejectionRow({ rejection, onClick }) {
  return (
    <div
      onClick={onClick}
      className="p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 cursor-pointer transition-all border border-yellow-200"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-[#172A3A]">{rejection.clientName || 'Unknown'}</div>
        <span className="bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded text-xs font-semibold">
          REJECTED
        </span>
      </div>
      <div className="text-sm text-gray-600">
        <span className="font-medium">Reason:</span> {rejection.rejection_reason || 'Not specified'}
      </div>
      {rejection.rejection_code && (
        <div className="text-xs text-gray-500 mt-1">Code: {rejection.rejection_code}</div>
      )}
    </div>
  );
}

// Win-Back Card
function WinBackCard({ client, onClick }) {
  return (
    <div
      onClick={onClick}
      className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 cursor-pointer transition-all border border-purple-200"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center">
          <User className="text-purple-600" size={20} />
        </div>
        <div>
          <div className="font-semibold text-[#172A3A]">{client.clientName || 'Unknown'}</div>
          <div className="text-sm text-purple-600">
            Left: {client.disenrollment_date ? new Date(client.disenrollment_date).toLocaleDateString() : 'Unknown'}
          </div>
        </div>
      </div>
      {client.disenrollment_reason && (
        <div className="mt-2 text-sm text-gray-600 truncate">
          Reason: {client.disenrollment_reason}
        </div>
      )}
    </div>
  );
}
